import { Badge, Skeleton, Table, Card , ConfigProvider } from 'antd';
import { fetchWorkspaceContracts } from 'app-redux/actions/contracts.action';
import { resetCurrentWorkspace } from 'app-redux/actions/licensingAccounts.actions';
import { getPlanById } from 'app-redux/actions/plans.actions';
import { getAppPublicInfoById } from 'app-redux/actions/storeApps.actions';
import { redeemVoucher } from 'app-redux/actions/vouchers.actions';
import { AppState } from 'app-redux/reducers';
import { getCurrentUser } from 'app-redux/selectors/accounts.selector';
import { getDefaultWorkspace } from 'app-redux/selectors/licensingAccounts.selector';
import { getProducts } from 'app-redux/selectors/products.selector';
import { IUserAccount } from 'core/accounts/models';
import { BasicAppDTO } from 'core/apps/models';
import { iff } from 'core/iff';
import { IWorkspace } from 'core/workspaces/models';
import { INavigationRouteProps } from 'core/navigation/models';
import { PlanDTO } from 'core/plans/models';
import { AppsStore } from 'core/products/models';
import { PageDTO, UUID } from 'core/utils/BasicModels';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { notification } from 'syngenta-digital-cropwise-react-ui-kit';
import { getWorkspaceById } from '../../app-redux/actions/workspace.actions';
import { WorkspaceResponse } from '../../core/workspace/models';
import {
  getCurrentSelectedPage,
  getDeepActionCBUrl,
  getDeepActionUrl,
  sortByKey,
  translationWrapper,
} from '../../utils/common-methods';
import { customizeRenderEmpty } from '../Members';
import './styles.less';
import { ContractDTO } from '../../core/contracts/models';
import { SegmentService } from 'services/SegmentService';
import { WorkspaceDetailsHeader } from 'components/Workspace/WorkspaceDetailsHeader/WorkspaceDetailsHeader';
import { find, get } from 'lodash';
import { RBAC_ACTIONS } from 'services/rbacActions';
import { verifyRBACPermission } from 'services/rbac';
import { useMediaQuery } from 'react-responsive';

/**
 * to display error message
 * @param props
 * @returns
 */
export const customizeRenderAPIErrorMessage = (props: any) => (
  <div className="empty-data-container" style={{ textAlign: 'center', color: 'red' }}>
       <p className="empty-data-title">{props.title}</p>
  </div>
);

interface IProductsData {
  product?: string;
  plan: string;
  start_date: string;
  waiting_for_payment: boolean;
  expiration_date: string;
  id: string;
  contract_status: string;
}
interface IProductsProps extends INavigationRouteProps<{}> {
  getWorkspaceById: (workspaceId: UUID) => Promise<WorkspaceResponse>;
  products?: AppsStore[];
  currentUser?: IUserAccount;
  currentWorkspace: IWorkspace | null;
  isProtectorBannerVisible: boolean;
  getPlanById: (planId: string) => Promise<PlanDTO>;
  getAppPublicInfoById: (appId: UUID) => Promise<BasicAppDTO>;
  resetCurrentWorkspace: () => any;
  redeemVoucher: (code: string, accountId: UUID) => Promise<any>;
  getWorkspaceContracts: (
    workspaceId: UUID,
    includeWaitingForPayment: boolean,
  ) => Promise<PageDTO<ContractDTO>>;
  accountId: any;
}

const NO_OF_PROPERTIES_PER_PAGE = 100;

const Accounts: React.FC<IProductsProps> = (props) => {
  const [productsData, setProductsData] = useState<IProductsData[]>();
  const [hasLoadedData, setHasLoadedData] = useState<boolean>(false);
  const [apiErrorMessage, setAPIErrorMessage] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [rbacPermissions, setRbacPermissions] = useState<any>([]);
  const [plansData, setPlansData] = useState<any>([]);
  const currentWorkspaceId = props.currentWorkspace?.id;
  const { t } = translationWrapper(useTranslation());
  const [lazyLoadedCount, setLazyLoadedCount] = useState({
    searchResult: NO_OF_PROPERTIES_PER_PAGE,
    result: NO_OF_PROPERTIES_PER_PAGE,
  });
  const errorHandler = (message: string, description: string) => {
    notification.error({
      message,
      description,
      placement: 'topRight',
    });
  };
  const isMobile = useMediaQuery({ query: '(max-width: 719px)' });

  const filterByTerm = (term: string) => {
    return productsData?.filter((data) => {
      const value = data.product;
      return iff(value !== '', value?.toLowerCase().includes(term.toLowerCase()));
    });
  };
  const handlePlanSearch = (searchInput: any) => {
    setSearchTerm(searchInput);
    setPlansData(filterByTerm(searchInput)?.slice(0, lazyLoadedCount.result));
  };
  const getTableSourceData = () => {
    const data = productsData?.slice(0, lazyLoadedCount.result) || [];
    setPlansData(data);
  };

  const isContractActive = (contract: ContractDTO) => {
    return moment(new Date()).isBetween(contract.start, contract.end);
  };

  const isContractPendingPayment = (contract: ContractDTO) => {
    return contract.waiting_for_payment;
  };

  const getContractCurrentStatus = (contract: ContractDTO) => {
    if (!isContractActive(contract)) {
      return 'inactive';
    }
    if (isContractPendingPayment(contract) && isContractActive(contract)) {
      return 'pending';
    }
    return 'active';
  };

  useEffect(() => {
    if (productsData) {
      getTableSourceData();
    }
  }, [productsData, lazyLoadedCount]);

  const getRBACPermissions = async () => {
    const extraRbacOperations = {
      operations: [
          {
            action: RBAC_ACTIONS.contract.new,
            resource: `crn:workspace:${currentWorkspaceId}`,
          },
        ],
    };

    const { data: { allowed_operations = [] } }: any = await verifyRBACPermission(extraRbacOperations);
    setRbacPermissions(allowed_operations);
  };

  useEffect(() => {
    const fetchAllData = async () => {
      if (props.currentWorkspace) {
        const workspace = await props.getWorkspaceById(props.currentWorkspace.id);
        const contracts = workspace.contracts;
        if (contracts) {
          // added code inside try catch, to catch errors if api fails
          try {
            const appDataPromiseResponse = await Promise.all(
              contracts.map((plansResponse: any) =>
                props.getAppPublicInfoById(plansResponse.app_id)
                  .catch((err: any) => {
                    console.error('Could not get public app info: ', err);
                  }))
                .filter((appData: any) => appData),
            );
            let productsFetched: any = contracts.reduce((acc: unknown[] , cnt: ContractDTO) => {
              const appData: any = find(appDataPromiseResponse, { id: cnt.app_id });
              if (appData) {
                acc.push({
                  product: appData.name,
                  icon_uri: appData.icon_uri,
                  plan: cnt.plan_name || '-',
                  start_date: cnt.start,
                  expiration_date: cnt.end,
                  waiting_for_payment: cnt.waiting_for_payment,
                  id: cnt.id,
                  contract_status: getContractCurrentStatus(cnt),
                });
              }
              return acc;
            }, []);

            productsFetched = productsFetched.filter((product: IProductsData) => product.product !== undefined) as IProductsData[];
            if (productsFetched.length > 0) {
              const sortedproductsFetched = sortByKey(productsFetched, 'product');
              setProductsData(sortedproductsFetched || productsFetched);
            } else {
              errorHandler(
                t('global.error.no_products_found'),
                t('global.error.we_are_sorry_the_page_you_requested_could_not_be_found'),
              );
            }
          } catch (error) {
            errorHandler(
              t('global.error.api_failed'),
              get(error, 'message', 'API failed'),
            );
            setAPIErrorMessage(get(error, 'message', 'API failed'));
          }
          setHasLoadedData(true);
        }
      }
    };
    fetchAllData().catch();
    getRBACPermissions();
  }, [props.currentWorkspace]);
  const columns = [
    {
      title: t('title.product'),
      sorter: (a: any, b: any) => a.product.localeCompare(b.product),
      dataIndex: 'product',
      key: 'product',
      render: (text: string) => text,
    },
    {
      title: t('title.plan'),
      sorter: (a: any, b: any) => a.plan.localeCompare(b.plan),
      dataIndex: 'plan',
      key: 'plan',
      render: (text: string) => (
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: t('title.expiration_date'),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      sorter: (a: any, b: any) => a.expiration_date.localeCompare(b.expiration_date),
      render: (text: string) => (
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="expiration-date-container"
        >
          {text ? moment(text).format(`${dateFormat} h:mm A`) : '--'}
        </div>
      ),
    },
    {
      title: t('title.status'),
      dataIndex: 'contract_status',
      key: 'contract_status',
      sorter: (a: any, b: any) => a.contract_status.localeCompare(b.contract_status),
      render: (text: string) => {
        if (text === 'inactive') {
          return (
            <div className="product_badge">
              {' '}
              <Badge status="default" text={t('global.status.inactive')} className="product_badge__inactive" />
            </div>
          );
        }
        if (text === 'pending') {
          return (
            <div className="product_badge">
              {' '}
              <Badge status="processing" text={t('global.status.pending')} className="product_badge__pending" />
            </div>
          );
        }
        return (
          <div className="product_badge">
            {' '}
            <Badge status="success" text={t('global.status.active')} className="product_badge__active" />
          </div>
        );
      },
    },
  ];

  const redirectToActivateNewPlanDeepAction = () => {
    SegmentService().track('Accounts - Products screen - Activate new plan');
    window.location.href = `${getDeepActionUrl()}/app/deep-actions/activate-new-plan?workspace=${currentWorkspaceId}&callback_uri=${getDeepActionCBUrl()}`;
  };

  const dateFormat = props.currentUser?.date_format || 'MM/DD/YYYY';

  const getDataForMobile = () => {
    return (plansData.length === 0 ? <>{customizeRenderEmpty({ t, title: 'info.no_plans_found' })}</> :
      plansData.map((product: any, key: any) => {
        // tslint:disable-next-line: jsx-wrap-multiline
        const status = product.contract_status.charAt(0).toUpperCase() + product.contract_status.slice(1);
        const textStatus = product.contract_status;
        const class_name = 'product_badge__' + product.contract_status;

        return (
          <>
            <Card key={key} title={product.product} >
              <div className="plansDataMainBox">
                <div className="plansDataleftBox">
                  <p>
                    <span>{t('title.plan')}</span>
                    <span>•</span>
                    {product.plan}
                  </p>
                  <p>
                    <span>{t('title.expiration_date')}</span>
                    <span>•</span>
                    {moment(product.expiration_date).format(dateFormat)}
                  </p>
                </div>
                <div className="plansDataRightBox">
                  <div className="product_badge">
                    <Badge status={(status === 'Inactive') ? 'default' : (status === 'Pending') ? 'processing' : 'success'}
                      text={t(`global.status.${textStatus}`)}
                      className={class_name}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </>
        );
      })
    );
  };

  return (
    <div className="product-page-cont">
      <div className="products-page-content">
        <WorkspaceDetailsHeader
          currentWorkspaceId={currentWorkspaceId}
          actionButton={{
            disabled: false, click: redirectToActivateNewPlanDeepAction, title: 'button.activate_new_plan' }}
          title={`global.title.${getCurrentSelectedPage()}`}
          setSearchTerm={handlePlanSearch}
          t={t}
          resetCurrentWorkspace={props.resetCurrentWorkspace}
        />
        <div className="products-page-table">
          {!hasLoadedData ? (
            <Skeleton loading={!hasLoadedData} active={true} />
          ) : (
            <div className="product-page-tablemain-div">
              <InfiniteScroll
                initialLoad={false}
                pageStart={0}
                loadMore={() => {
                  if (
                    !(productsData && lazyLoadedCount.result >= productsData.length) &&
                    searchTerm === ''
                  ) {
                    setLazyLoadedCount({
                      ...lazyLoadedCount,
                      result: lazyLoadedCount.result + NO_OF_PROPERTIES_PER_PAGE,
                    });
                  }
                }}
                hasMore={true}
                useWindow={false}
              >
                {isMobile ? getDataForMobile() : (
                  <ConfigProvider
                      renderEmpty={
                    () => apiErrorMessage !== '' ?
                        customizeRenderAPIErrorMessage({ title: apiErrorMessage })
                        : customizeRenderEmpty({ t, title: 'info.no_plans_found' })}
                  >
                  <Table
                    className="products-table"
                    rowKey={(record: any, index: any) => record?.id + index + ''}
                    columns={columns}
                    dataSource={plansData}
                    pagination={false}
                  />
                  </ConfigProvider>
                )}
              </InfiniteScroll>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  products: getProducts(state),
  currentUser: getCurrentUser(state),
  accountId: getDefaultWorkspace(state),
  currentWorkspaceId: getDefaultWorkspace(state),
  currentWorkspace: find(get(state, 'workspaces.workspaces', []), { id: getDefaultWorkspace(state) }),
  isProtectorBannerVisible:
    !state.rollout.loading && state.rollout?.flags?.isProtectorBannerVisible,
});

const mapDispatchToProps = (dispatch: any) => ({
  getWorkspaceById: (workspaceId: UUID) => dispatch(getWorkspaceById(workspaceId)),
  getPlanById: (planId: string) => dispatch(getPlanById(planId)),
  getAppPublicInfoById: (appId: UUID) => dispatch(getAppPublicInfoById(appId)),
  resetCurrentWorkspace: () => dispatch(resetCurrentWorkspace()),
  getWorkspaceContracts: (workspaceId: UUID, includeWaitingForPayment: boolean) =>
    dispatch(fetchWorkspaceContracts(workspaceId, includeWaitingForPayment)),
  redeemVoucher: (code: string, accountId: UUID) => dispatch(redeemVoucher(code, accountId)),
});

export const ProductsPage = withRouter(connect(mapStateToProps, mapDispatchToProps)(Accounts));
